/**
 * DataBaselineView -- the Day-1 data baseline (P339).
 *
 * Route: /admin/data-baseline. Composes the three source-data quality
 * dimensions -- structural linkage, SAMA-mandated completeness, and
 * classification discrimination (P336) -- into one verdict. The baseline
 * gates the resilience score's CONFIDENCE, not usage: weak source data
 * honestly caps the tier instead of producing confident-looking dashboards.
 *
 * Hardcoded English to match the sibling exec/admin views.
 */
import { said } from "shared/i18n/said";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { BaselinePillar, DataBaseline } from "shared/api/types";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useDataBaseline } from "shared/admin/dataBaselineHooks";

const VERDICT_TONE: Record<DataBaseline["verdict"], string> = {
  established: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  partial: "bg-amber-100 text-amber-800 ring-amber-300",
  thin: "bg-rose-100 text-rose-800 ring-rose-300",
};

const VERDICT_LABEL: Record<DataBaseline["verdict"], string> = {
  established: "Established",
  partial: "Partial",
  thin: "Thin",
};

function Bar({ pct }: { pct: number }): JSX.Element {
  const tone =
    pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="h-1.5 w-full rounded bg-slate-100">
      <div
        className={`h-1.5 rounded ${tone}`}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

function PillarCard({ pillar }: { pillar: BaselinePillar }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`data-baseline-pillar-${pillar.key}`}
      className="space-y-2 rounded-xl border border-paper-2 bg-white p-4 shadow-card"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-slate-800">
          {said(t, `enumWords.baselinePillar.${pillar.key}`, pillar.label)}
        </p>
        <span className="font-display text-xl text-slate-900 tabular-nums">
          {pillar.pct}%
        </span>
      </div>
      <Bar pct={pillar.pct} />
      {/* Round 19 I23 -- composed in the backend, it read in English
          under any language; the key and figures read in the reader's. */}
      <p
        data-testid={`data-baseline-pillar-detail-${pillar.key}`}
        className="text-xs text-slate-500"
      >
        {pillar.detail_key
          ? said(t, pillar.detail_key, pillar.detail, pillar.detail_params ?? {})
          : pillar.detail}
      </p>
      {pillar.ratios.length > 0 && (
        <ul className="space-y-1 pt-1">
          {pillar.ratios.map((r) => (
            <li
              key={r.key}
              data-testid={`data-baseline-ratio-${r.key}`}
              className="flex items-center justify-between gap-3 text-xs"
            >
              <span className="text-slate-600">
                {said(t, `enumWords.baselineRatio.${r.key}`, r.label)}
                {/* ROUND 13 D10 -- a rung that scores a bank 0 / 2 and
                    caps its resilience-score confidence should say
                    where the gap is closed. The channel rung could not:
                    nothing in the product populated it, and the IBS
                    template's `channels` column was free text three
                    screens from anything that read it. */}
                {r.fix_entity_kind && r.numerator < r.denominator ? (
                  <Link
                    to={`/admin/data-import?kind=${encodeURIComponent(r.fix_entity_kind)}`}
                    data-testid={`data-baseline-fix-${r.key}`}
                    className="ms-2 text-brand-brassDark hover:underline"
                  >
                    {t("screens.dataBaselineView.fixThis")}
                  </Link>
                ) : null}
              </span>
              <span className="whitespace-nowrap text-slate-500 tabular-nums">
                {r.numerator} / {r.denominator} · {r.pct}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DataBaselineView(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useDataBaseline();

  return (
    <article data-testid="data-baseline-view" className="space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="obligation"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("screens.dataBaselineView.dataBaseline")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("screens.dataBaselineView.everyDerivedSurfaceBlastCascadeWarRoom")}{" "}
          <span className="font-medium">
            {t("screens.dataBaselineView.structuralLinkage")}
          </span>
          ,{" "}
          <span className="font-medium">
            {t("screens.dataBaselineView.samaMandatedCompleteness")}
          </span>{" "}
          {t("screens.dataBaselineView.and")}{" "}
          <span className="font-medium">
            {t("screens.dataBaselineView.classificationDiscrimination")}
          </span>{" "}
          {t("screens.dataBaselineView.intoOneFigureThatFeedsTheScore")}
        </p>
      </header>

      {isLoading ? (
        <div data-testid="data-baseline-loading" className="text-sm text-slate-500">
          {t("screens.dataBaselineView.loading")}
        </div>
      ) : isError || !data ? (
        <div data-testid="data-baseline-error" className="text-sm text-slate-500">
          {t("screens.dataBaselineView.baselineUnavailable")}
        </div>
      ) : (
        <>
          <section
            data-testid="data-baseline-summary"
            className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
          >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="font-display text-3xl text-slate-900 tabular-nums">
                {data.baseline_pct}%
              </span>
              <span
                data-testid="data-baseline-verdict"
                className={`rounded px-2 py-0.5 text-xs font-semibold ring-1 ${VERDICT_TONE[data.verdict]}`}
              >
                {VERDICT_LABEL[data.verdict]}
              </span>
              <span className="text-sm text-slate-600">
                {t("screens.dataBaselineView.composedSourceDataBaseline")}
              </span>
              {data.coherence_flag_count > 0 && (
                <Link
                  to="/twin/field-exam"
                  className="text-sm text-brand-brassDark hover:underline"
                >
                  {data.coherence_flag_count}{" "}
                  {t("screens.dataBaselineView.coherenceFlag")}
                  {data.coherence_flag_count === 1 ? "" : "s"}{" "}
                  {t("screens.dataBaselineView.onTheFieldExamView")}
                </Link>
              )}
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-3">
            {data.pillars.map((p) => (
              <PillarCard key={p.key} pillar={p} />
            ))}
          </section>

          <section className="flex flex-wrap gap-4 text-sm">
            <Link
              to="/admin/data-import"
              className="text-brand-brassDark hover:underline"
            >
              {t("screens.dataBaselineView.importDataCsvAssisted")}
            </Link>
            <Link
              to="/admin/data-dictionary"
              className="text-brand-brassDark hover:underline"
            >
              {t("screens.dataBaselineView.dataDictionaryTemplates")}
            </Link>
            <Link
              to="/twin/field-exam"
              className="text-brand-brassDark hover:underline"
            >
              {t("screens.dataBaselineView.fieldExamReadiness")}
            </Link>
            <Link to="/readiness" className="text-brand-brassDark hover:underline">
              {t("screens.dataBaselineView.resilienceReadinessScore")}
            </Link>
          </section>
        </>
      )}
    </article>
  );
}
